import { useState, useCallback } from 'react';
import { apiService } from '../../services';
import {
  Article,
  ArticleCreate,
  Event,
  EventCreate,
  Album,
  AlbumCreate,
  Book,
  BookCreate,
  Canticle,
  CanticleCreate,
} from '../../types';

type SelectedType = 'articles' | 'events' | 'albums' | 'canticles' | 'books';

interface UseItemHandlersProps {
  onSuccess: () => Promise<void>;
}

type CreateResult = Article | Event | Album | Book | Canticle | void;

export const useItemHandlers = ({ onSuccess }: UseItemHandlersProps) => {
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleCreate = useCallback(
    async (
      type: SelectedType,
      data: ArticleCreate | EventCreate | AlbumCreate | BookCreate | CanticleCreate,
      files?: File | FileList
    ): Promise<CreateResult> => {
      setFormLoading(true);
      setFormError(null);
      try {
        switch (type) {
          case 'articles': {
            const article = await apiService.createArticle(data as ArticleCreate);
            if (files instanceof File) {
              try {
                await apiService.uploadArticleImage(article.id, files);
              } catch (uploadErr) {
                console.error('Failed to upload article image:', uploadErr);
              }
            }
            await onSuccess();
            return article;
          }
          case 'events': {
            const event = await apiService.createEvent(data as EventCreate);
            if (files instanceof File) {
              try {
                await apiService.uploadEventImage(event.id, files);
              } catch (uploadErr) {
                console.error('Failed to upload event image:', uploadErr);
              }
            }
            await onSuccess();
            return event;
          }
          case 'albums': {
            const albumData = data as AlbumCreate & { files?: FileList };
            const album = await apiService.createAlbum({
              name: albumData.name,
              description: albumData.description,
              status: albumData.status,
            });
            if (files instanceof FileList && files.length > 0) {
              for (let i = 0; i < files.length; i++) {
                const file = files[i];
                try {
                  await apiService.uploadAlbumMedia(album.id, file);
                } catch (uploadErr) {
                  console.error(`Failed to upload file ${file.name}:`, uploadErr);
                }
              }
            }
            await onSuccess();
            return album;
          }
          case 'canticles': {
            const canticle = await apiService.createCanticle(data as CanticleCreate);
            if (files instanceof File) {
              try {
                await apiService.uploadCanticlePdf(canticle.id, files);
              } catch (uploadErr) {
                console.error('Failed to upload canticle PDF:', uploadErr);
              }
            }
            await onSuccess();
            return canticle;
          }
          case 'books': {
            const book = await apiService.createBook(data as BookCreate);
            const fileArray = files instanceof FileList ? Array.from(files) : files instanceof File ? [files] : [];
            const pdfFile = fileArray.find(f => f.type === 'application/pdf');
            const coverFile = fileArray.find(f => f.type.startsWith('image/'));
            
            if (pdfFile) {
              try {
                const result = await apiService.uploadBookPdf(book.id, pdfFile);
                await apiService.updateBook(book.id, { pdf_path: result.pdf_path });
              } catch (uploadErr) {
                console.error('Failed to upload book PDF:', uploadErr);
              }
            }
            if (coverFile) {
              try {
                const result = await apiService.uploadBookCover(book.id, coverFile);
                await apiService.updateBook(book.id, { cover_image: result.cover_image });
              } catch (uploadErr) {
                console.error('Failed to upload book cover:', uploadErr);
              }
            }
            await onSuccess();
            return book;
          }
        }
      } catch (err: any) {
        console.error(`Failed to create ${type}:`, err);
        setFormError(`Erreur lors de la création`);
      } finally {
        setFormLoading(false);
      }
    },
    [onSuccess]
  );

  const handleUpdate = useCallback(
    async (
      id: number,
      type: SelectedType,
      data: Partial<ArticleCreate | EventCreate | AlbumCreate | BookCreate | CanticleCreate>,
      files?: File | FileList
    ): Promise<Article | Event | Album | Book | Canticle | void> => {
      setFormLoading(true);
      setFormError(null);
      try {
        switch (type) {
          case 'articles': {
            const article = await apiService.updateArticle(id, data as Partial<ArticleCreate>);
            if (files instanceof File) {
              try {
                await apiService.uploadArticleImage(id, files);
              } catch (uploadErr) {
                console.error('Failed to upload article image:', uploadErr);
              }
            }
            await onSuccess();
            return article;
          }
          case 'events': {
            const event = await apiService.updateEvent(id, data as Partial<EventCreate>);
            if (files instanceof File) {
              try {
                await apiService.uploadEventImage(id, files);
              } catch (uploadErr) {
                console.error('Failed to upload event image:', uploadErr);
              }
            }
            await onSuccess();
            return event;
          }
          case 'canticles': {
            const canticle = await apiService.updateCanticle(id, data as Partial<CanticleCreate>);
            if (files instanceof File) {
              try {
                await apiService.uploadCanticlePdf(id, files);
              } catch (uploadErr) {
                console.error('Failed to upload canticle PDF:', uploadErr);
              }
            }
            await onSuccess();
            return canticle;
          }
          case 'books': {
            const book = await apiService.updateBook(id, data as Partial<BookCreate>);
            const fileArray = files instanceof FileList ? Array.from(files) : files instanceof File ? [files] : [];
            const pdfFile = fileArray.find(f => f.type === 'application/pdf');
            const coverFile = fileArray.find(f => f.type.startsWith('image/'));
            
            if (pdfFile) {
              try {
                const result = await apiService.uploadBookPdf(id, pdfFile);
                await apiService.updateBook(id, { pdf_path: result.pdf_path });
              } catch (uploadErr) {
                console.error('Failed to upload book PDF:', uploadErr);
              }
            }
            if (coverFile) {
              try {
                const result = await apiService.uploadBookCover(id, coverFile);
                await apiService.updateBook(id, { cover_image: result.cover_image });
              } catch (uploadErr) {
                console.error('Failed to upload book cover:', uploadErr);
              }
            }
            await onSuccess();
            return book;
          }
          default:
            await onSuccess();
            return;
        }
      } catch (err: any) {
        console.error(`Failed to update ${type}:`, err);
        setFormError(`Erreur lors de la mise à jour`);
        return;
      } finally {
        setFormLoading(false);
      }
    },
    [onSuccess]
  );

  const handleDelete = useCallback(
    async (type: SelectedType, id: number) => {
      const confirmMessages: Record<SelectedType, string> = {
        articles: "Êtes-vous sûr de vouloir supprimer cet article ?",
        events: "Êtes-vous sûr de vouloir supprimer cet événement ?",
        albums: "Êtes-vous sûr de vouloir supprimer cet album ?",
        canticles: "Êtes-vous sûr de vouloir supprimer ce cantique ?",
        books: "Êtes-vous sûr de vouloir supprimer cette édition ?",
      };

      if (!window.confirm(confirmMessages[type])) return;

      try {
        switch (type) {
          case 'articles':
            await apiService.deleteArticle(id);
            break;
          case 'events':
            await apiService.deleteEvent(id);
            break;
          case 'albums':
            await apiService.deleteAlbum(id);
            break;
          case 'canticles':
            await apiService.deleteCanticle(id);
            break;
          case 'books':
            await apiService.deleteBook(id);
            break;
        }
        await onSuccess();
      } catch (err) {
        console.error(`Failed to delete ${type}:`, err);
        setFormError(`Erreur lors de la suppression`);
      }
    },
    [onSuccess]
  );

  const handlePublish = useCallback(
    async (type: SelectedType, id: number) => {
      setFormLoading(true);
      setFormError(null);
      try {
        switch (type) {
          case 'articles':
            await apiService.updateArticle(id, { status: 'published' as any });
            break;
          case 'events':
            await apiService.updateEvent(id, { status: 'published' as any });
            break;
          case 'albums':
            await apiService.updateAlbum(id, { status: 'published' as any });
            break;
          case 'canticles':
            await apiService.updateCanticle(id, { status: 'published' as any });
            break;
          case 'books':
            await apiService.updateBook(id, { is_published: true });
            break;
        }
        await onSuccess();
      } catch (err: any) {
        console.error(`Failed to publish ${type}:`, err);
        setFormError(`Erreur lors de la publication`);
      } finally {
        setFormLoading(false);
      }
    },
    [onSuccess]
  );

  return {
    handleCreate,
    handleUpdate,
    handleDelete,
    handlePublish,
    formLoading,
    formError,
    setFormError,
  };
};
